import { Fragment } from 'react';
import { LegalDocumentContent } from '../types';

/**
 * Turns one section's stored text into elements.
 *
 * The grammar is two rules: a blank line starts a new paragraph, and a
 * line beginning "- " is a bullet. Consecutive bullets become one list.
 *
 * Text, never HTML. A Super Admin writes this and every anonymous
 * visitor renders it, so `dangerouslySetInnerHTML` here would turn the
 * editor into stored XSS against the whole public site. React escaping
 * the text is the entire defence, and it is why the grammar stays too
 * small to be worth extending by hand — if richer formatting is ever
 * needed, it wants a real Markdown renderer plus sanitising, not one
 * more special case here.
 */
function renderLegalBody(body: string, keyPrefix: string) {
  const blocks: JSX.Element[] = [];
  // Split on blank lines first; each chunk is then either a run of
  // bullets or a paragraph.
  const chunks = body.split(/\n\s*\n/);

  chunks.forEach((chunk, chunkIndex) => {
    const lines = chunk.split('\n').map((line) => line.trim()).filter(Boolean);
    if (lines.length === 0) return;

    const bullets = lines.filter((line) => line.startsWith('- '));
    // A chunk is a list when every line in it is a bullet. A stray
    // paragraph sharing a chunk with bullets stays a paragraph rather
    // than being silently swallowed into the list.
    if (bullets.length === lines.length) {
      blocks.push(
        <ul key={`${keyPrefix}-l${chunkIndex}`}>
          {lines.map((line, i) => (
            <li key={i}>{line.slice(2)}</li>
          ))}
        </ul>
      );
      return;
    }

    lines.forEach((line, i) => {
      blocks.push(<p key={`${keyPrefix}-p${chunkIndex}-${i}`}>{line.startsWith('- ') ? line.slice(2) : line}</p>);
    });
  });

  return blocks;
}

interface LegalDocumentViewProps {
  content: LegalDocumentContent;
  /**
   * Leaves out the title and date, for where the document is embedded
   * under a heading of its own — the consent box on the registration
   * form shows the policy inside a labelled, scrollable panel and does
   * not want a second title inside it.
   */
  headless?: boolean;
}

/**
 * Renders a stored legal document. Shared deliberately: the public page
 * and the box an applicant ticks to agree must show the same text, and
 * the surest way to guarantee that is for them to be the same component
 * reading the same row.
 */
const LegalDocumentView = ({ content, headless }: LegalDocumentViewProps) => (
  <>
    {!headless && (
      <>
        <h1 style={{ marginTop: '1rem', marginBottom: '0.25rem' }}>{content.title}</h1>
        {content.lastUpdated && (
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: 0 }}>
            Last updated: {content.lastUpdated}
          </p>
        )}
      </>
    )}

    {content.draft && (
      <div
        className="alert-error"
        style={{ padding: '0.85rem 1rem', margin: '1rem 0', fontSize: '0.85rem' }}
        role="alert"
      >
        <strong>Draft — not yet in force.</strong> This text still contains items the programme has to decide
        and has not been through legal review.
      </div>
    )}

    <div className="legal-body">
      {content.sections.map((section, index) => (
        <Fragment key={index}>
          <h2 style={{ fontSize: '1.05rem', marginTop: index === 0 ? '1rem' : '1.75rem', marginBottom: '0.4rem' }}>
            {section.heading}
          </h2>
          {renderLegalBody(section.body, `s${index}`)}
        </Fragment>
      ))}
    </div>
  </>
);

export default LegalDocumentView;
